/**
 * AlertDashboard Component
 *
 * Dashboard component showing alert statistics, filters, and alert list.
 * Used on the main alerts page.
 */

import React, { useState, useEffect } from 'react';
import { useAlerts } from '../contexts/AlertContext';
import { AlertSeverity, AlertType, realtimeAlertApi } from '../services/realtime-alert.service';
import type { Alert } from '../services/realtime-alert.service';
import AlertCard from './AlertCard';
import { getSeverityColor } from '../utils/browser-notifications';
import { logger } from '../utils/logger';

interface AlertDashboardProps {
  selectedAlertId?: string;
}

interface AlertStats {
  total: number;
  unread: number;
  bySeverity: {
    critical: number;
    high: number;
    warning: number;
    info: number;
  };
  byType: {
    shellGame: number;
    contactMatch: number;
    fatalCrash: number;
  };
}

const AlertDashboard: React.FC<AlertDashboardProps> = ({ selectedAlertId }) => {
  const {
    alerts,
    unreadCount,
    isConnected,
    isLoading,
    markAsRead,
    markAllAsRead,
    acknowledgeAlert,
    dismissAlert,
    refreshAlerts,
  } = useAlerts();

  const [stats, setStats] = useState<AlertStats | null>(null);
  const [filter, setFilter] = useState<{
    severities: AlertSeverity[];
    types: AlertType[];
    unreadOnly: boolean;
  }>({
    severities: [],
    types: [],
    unreadOnly: false,
  });
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);

  // Load stats on mount
  useEffect(() => {
    const loadStats = async () => {
      try {
        const fetchedStats = await realtimeAlertApi.getAlertStats();
        setStats(fetchedStats);
      } catch (error) {
        logger.error('Error loading alert stats:', { error });
      }
    };
    loadStats();
  }, [alerts]);

  // Set selected alert based on URL param
  useEffect(() => {
    if (selectedAlertId) {
      const alert = alerts.find(a => a.id === selectedAlertId);
      setSelectedAlert(alert || null);
    }
  }, [selectedAlertId, alerts]);

  // Filter alerts
  const filteredAlerts = alerts.filter(alert => {
    if (filter.unreadOnly && alert.readAt) return false;
    if (filter.severities.length > 0 && !filter.severities.includes(alert.severity)) return false;
    if (filter.types.length > 0 && !filter.types.includes(alert.alertType)) return false;
    return true;
  });

  // Toggle severity filter
  const toggleSeverityFilter = (severity: AlertSeverity) => {
    setFilter(prev => ({
      ...prev,
      severities: prev.severities.includes(severity)
        ? prev.severities.filter(s => s !== severity)
        : [...prev.severities, severity],
    }));
  };

  // Toggle type filter
  const toggleTypeFilter = (type: AlertType) => {
    setFilter(prev => ({
      ...prev,
      types: prev.types.includes(type)
        ? prev.types.filter(t => t !== type)
        : [...prev.types, type],
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilter({
      severities: [],
      types: [],
      unreadOnly: false,
    });
  };

  // Handle alert click
  const handleAlertClick = (alert: Alert) => {
    setSelectedAlert(alert);
    if (!alert.readAt) {
      markAsRead(alert.id);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Alerts</h1>
            <p className="text-sm text-gray-500 mt-1">
              {unreadCount > 0
                ? `${unreadCount} unread alert${unreadCount !== 1 ? 's' : ''}`
                : 'All caught up!'}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Connection Status */}
            <span
              className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded-full ${
                isConnected
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  isConnected ? 'bg-green-500' : 'bg-red-500'
                }`}
              />
              {isConnected ? 'Live' : 'Disconnected'}
            </span>

            {/* Refresh Button */}
            <button
              onClick={refreshAlerts}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
              title="Refresh"
            >
              <svg
                className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>

            {/* Mark All Read Button */}
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-md"
              >
                Mark all read
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 border-b border-gray-200">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <p className="text-sm text-gray-500">Total Alerts</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <p className="text-sm text-gray-500">Unread</p>
            <p className="text-2xl font-bold text-blue-600">{stats.unread}</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <p className="text-sm text-gray-500">Critical</p>
            <p className="text-2xl font-bold text-red-600">{stats.bySeverity.critical}</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <p className="text-sm text-gray-500">High Priority</p>
            <p className="text-2xl font-bold text-orange-600">{stats.bySeverity.high}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-500">Filter:</span>

          {/* Unread Only */}
          <button
            onClick={() => setFilter(prev => ({ ...prev, unreadOnly: !prev.unreadOnly }))}
            className={`px-3 py-1 text-sm rounded-full ${
              filter.unreadOnly
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Unread only
          </button>

          {/* Severity Filters */}
          {Object.values(AlertSeverity).map(severity => (
            <button
              key={severity}
              onClick={() => toggleSeverityFilter(severity)}
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                filter.severities.includes(severity)
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={{
                backgroundColor: filter.severities.includes(severity)
                  ? getSeverityColor(severity)
                  : undefined,
              }}
            >
              {severity}
            </button>
          ))}

          {/* Clear Filters */}
          {(filter.severities.length > 0 || filter.types.length > 0 || filter.unreadOnly) && (
            <button
              onClick={clearFilters}
              className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Alert List */}
        <div className="flex-1 overflow-y-auto p-4">
          {filteredAlerts.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="w-16 h-16 mx-auto text-gray-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No alerts</h3>
              <p className="mt-2 text-sm text-gray-500">
                {filter.severities.length > 0 || filter.types.length > 0 || filter.unreadOnly
                  ? 'Try adjusting your filters'
                  : 'You\'re all caught up!'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredAlerts.map(alert => (
                <AlertCard
                  key={alert.id}
                  alert={alert}
                  onMarkAsRead={markAsRead}
                  onAcknowledge={acknowledgeAlert}
                  onDismiss={dismissAlert}
                  onClick={handleAlertClick}
                />
              ))}
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selectedAlert && (
          <div className="w-96 border-l border-gray-200 bg-white overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Alert Details</h2>
              <button
                onClick={() => setSelectedAlert(null)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4">
              {/* Alert Info */}
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase">Title</label>
                  <p className="text-gray-900 font-medium">{selectedAlert.title}</p>
                </div>

                <div>
                  <label className="text-xs text-gray-500 uppercase">Message</label>
                  <p className="text-gray-700">{selectedAlert.message}</p>
                </div>

                {selectedAlert.dotNumber && (
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Carrier DOT</label>
                    <p className="text-blue-600">{selectedAlert.dotNumber}</p>
                  </div>
                )}

                <div>
                  <label className="text-xs text-gray-500 uppercase">Type</label>
                  <p className="text-gray-700">{selectedAlert.alertType}</p>
                </div>

                <div>
                  <label className="text-xs text-gray-500 uppercase">Severity</label>
                  <p
                    className="font-medium"
                    style={{ color: getSeverityColor(selectedAlert.severity) }}
                  >
                    {selectedAlert.severity}
                  </p>
                </div>

                <div>
                  <label className="text-xs text-gray-500 uppercase">Created</label>
                  <p className="text-gray-700">
                    {new Date(selectedAlert.createdAt).toLocaleString()}
                  </p>
                </div>

                {/* Data/Details */}
                {selectedAlert.data && Object.keys(selectedAlert.data).length > 0 && (
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Details</label>
                    <pre className="mt-2 p-3 bg-gray-50 rounded-lg text-xs overflow-x-auto">
                      {JSON.stringify(selectedAlert.data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="mt-6 space-y-2">
                {!selectedAlert.readAt && (
                  <button
                    onClick={() => markAsRead(selectedAlert.id)}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Mark as Read
                  </button>
                )}
                {!selectedAlert.acknowledgedAt && (
                  <button
                    onClick={() => acknowledgeAlert(selectedAlert.id)}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Acknowledge
                  </button>
                )}
                <button
                  onClick={() => {
                    dismissAlert(selectedAlert.id);
                    setSelectedAlert(null);
                  }}
                  className="w-full px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertDashboard;
